import React, { useEffect, useState } from 'react';
import { Edit2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { careersService, CareerPayload } from '../../services/api';
import { Career } from '../../pages/CareersPage';

interface FormState {
  position_title: string;
  job_type: 'fulltime' | 'parttime';
  qualifications: string;
  experience: string;
  location_type: 'onsite' | 'virtual';
  skills: string;
  added_advantage: string;
  application_deadline: string;
  comments: string;
  application_email: string;
}

const emptyForm: FormState = {
  position_title: '',
  job_type: 'fulltime',
  qualifications: '',
  experience: '',
  location_type: 'onsite',
  skills: '',
  added_advantage: '',
  application_deadline: '',
  comments: '',
  application_email: ''
};

// Converts an ISO timestamp to the value format of a datetime-local input
const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  const pad = (n: number) => ('0' + n).slice(-2);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const inputClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none';

const AdminCareers: React.FC = () => {
  const [careers, setCareers] = useState<Career[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);

  const loadCareers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await careersService.getAllCareers();
      setCareers(response.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load careers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCareers();
  }, []);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  const startAdd = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  };

  const startEdit = (career: Career) => {
    setEditingId(career.id);
    setShowForm(true);
    setForm({
      position_title: career.position_title,
      job_type: career.job_type,
      qualifications: career.qualifications,
      experience: career.experience || '',
      location_type: career.location_type,
      skills: (career.skills_required || []).join(', '),
      added_advantage: career.added_advantage || '',
      application_deadline: toLocalInput(career.application_deadline),
      comments: career.comments || '',
      application_email: career.application_email
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!editingId && new Date(form.application_deadline).getTime() <= Date.now()) {
      setError('Application deadline cannot be in the past');
      return;
    }

    const payload: CareerPayload = {
      position_title: form.position_title.trim(),
      job_type: form.job_type,
      qualifications: form.qualifications.trim(),
      experience: form.experience.trim(),
      location_type: form.location_type,
      skills_required: form.skills.split(/[,\n]/).map((s) => s.trim()).filter(Boolean),
      added_advantage: form.added_advantage.trim(),
      application_deadline: new Date(form.application_deadline).toISOString(),
      comments: form.comments.trim(),
      application_email: form.application_email.trim()
    };

    try {
      setSaving(true);
      if (editingId) {
        await careersService.updateCareer(editingId, payload);
        setSuccess('Career updated successfully');
      } else {
        await careersService.createCareer(payload);
        setSuccess('Career created successfully');
      }
      resetForm();
      await loadCareers();
    } catch (err: any) {
      setError(err?.response?.data?.message || (err instanceof Error ? err.message : 'Failed to save career'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this career?')) return;
    try {
      setError(null);
      await careersService.deleteCareer(id);
      setSuccess('Career deleted successfully');
      if (editingId === id) resetForm();
      await loadCareers();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete career');
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Careers</h2>
          <p className="text-gray-600">Add and update available positions</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={startAdd}
            disabled={showForm && !editingId}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          >
            <Plus className="h-4 w-4" />
            Add Career
          </button>
          <button
            onClick={loadCareers}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}
      {success && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{success}</div>
      )}

      {/* Grid-row transition animates height between 0 and content height */}
      <div
        className="grid transition-all duration-300 ease-in-out"
        style={{ gridTemplateRows: showForm ? '1fr' : '0fr', opacity: showForm ? 1 : 0 }}
        aria-hidden={!showForm}
      >
        <div className="overflow-hidden">
      <form onSubmit={handleSubmit} className="rounded-lg bg-white p-4 shadow space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">{editingId ? 'Edit Career' : 'Add Career'}</h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Position title</label>
            <input required className={inputClass} value={form.position_title} onChange={(e) => setField('position_title', e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Application email</label>
            <input required type="email" className={inputClass} value={form.application_email} onChange={(e) => setField('application_email', e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Job type</label>
            <select className={inputClass} value={form.job_type} onChange={(e) => setField('job_type', e.target.value as FormState['job_type'])}>
              <option value="fulltime">Full-time</option>
              <option value="parttime">Part-time</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Location</label>
            <select className={inputClass} value={form.location_type} onChange={(e) => setField('location_type', e.target.value as FormState['location_type'])}>
              <option value="onsite">On-site</option>
              <option value="virtual">Virtual</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Application deadline</label>
            <input required type="datetime-local" min={editingId ? undefined : toLocalInput(new Date().toISOString())} className={inputClass} value={form.application_deadline} onChange={(e) => setField('application_deadline', e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Skills required (comma separated)</label>
            <input className={inputClass} value={form.skills} onChange={(e) => setField('skills', e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Qualifications</label>
            <textarea required rows={3} className={inputClass} value={form.qualifications} onChange={(e) => setField('qualifications', e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Experience</label>
            <textarea rows={3} className={inputClass} value={form.experience} onChange={(e) => setField('experience', e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Added advantage</label>
            <textarea rows={3} className={inputClass} value={form.added_advantage} onChange={(e) => setField('added_advantage', e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Comments / notes</label>
            <textarea rows={3} className={inputClass} value={form.comments} onChange={(e) => setField('comments', e.target.value)} />
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          >
            <Plus className="h-4 w-4" />
            {editingId ? 'Update Career' : 'Add Career'}
          </button>
          <button type="button" onClick={resetForm} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
        </div>
      </form>
        </div>
      </div>

      <div className="rounded-lg bg-white p-4 shadow overflow-x-auto">
        <h3 className="mb-3 text-lg font-semibold text-gray-900">All Careers</h3>
        {careers.length === 0 ? (
          <p className="text-sm text-gray-600">{loading ? 'Loading...' : 'No careers added yet.'}</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="py-2 pr-4">Position</th>
                <th className="py-2 pr-4">Type</th>
                <th className="py-2 pr-4">Location</th>
                <th className="py-2 pr-4">Deadline</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {careers.map((career) => {
                const expired = new Date(career.application_deadline).getTime() <= Date.now();
                return (
                  <tr key={career.id} className="border-b last:border-0">
                    <td className="py-2 pr-4 font-medium text-gray-900">{career.position_title}</td>
                    <td className="py-2 pr-4">{career.job_type === 'fulltime' ? 'Full-time' : 'Part-time'}</td>
                    <td className="py-2 pr-4">{career.location_type === 'onsite' ? 'On-site' : 'Virtual'}</td>
                    <td className="py-2 pr-4">{new Date(career.application_deadline).toLocaleString()}</td>
                    <td className="py-2 pr-4">
                      <span className={expired ? 'text-rose-700' : 'text-emerald-700'}>{expired ? 'Closed' : 'Open'}</span>
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button onClick={() => startEdit(career)} className="p-1 text-blue-600 hover:text-blue-800" aria-label="Edit">
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(career.id)} className="p-1 text-rose-600 hover:text-rose-800" aria-label="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminCareers;
